import React, { useEffect } from 'react';
import { colors } from '../styles/tokens';
import { useEsMovil } from '../hooks/useEsMovil';

/**
 * "Modo enfoque" de una tabla del tablero — el equivalente al botón *Expandir*
 * de Power BI: el mismo objeto, a pantalla casi completa, sin nada alrededor.
 *
 * POR QUÉ EXISTE
 * Las tablas ya tenían "Ver todas", que quita el recorte al top N y el tope de
 * alto. El problema que reportó el usuario es que al expandirse **se pierde el
 * hilo**: la tabla crece dentro de la página, empuja lo de abajo y hay que
 * buscar dónde quedó. En Power BI el objeto se despega y ocupa la pantalla, y
 * al cerrarlo todo vuelve a su sitio.
 *
 * CÓMO
 * No duplica la tabla ni la vuelve a montar: **envuelve el MISMO contenido** y
 * sólo cambia el contenedor. Por eso el componente que lo usa conserva su
 * estado —el ordenamiento por columna, el "Ver todas", la fila resaltada— al
 * entrar y salir del enfoque, y su cabecera sigue siendo la suya, con su propio
 * botón de exportar a Excel.
 *
 * Se cierra con Escape, con el clic fuera o con la ✕, igual que los demás
 * modales del tablero (`NegadosDrillModal`, `ModeloDetalleModal`).
 */
export interface ModalEnfoqueProps {
  /** Título de la barra superior del modal. */
  titulo: string;
  /** Línea secundaria opcional: periodo, nº de filas, alcance. */
  subtitulo?: string;
  onClose: () => void;
  /**
   * Acciones de la tabla —exportar a Excel, sobre todo— que suben a ESTA barra.
   * En modo enfoque la tabla oculta su propia cabecera: una sola barra de
   * título con las acciones dentro, como el Expandir de Power BI. Dos
   * cabeceras apiladas se comían el alto y la de arriba quedaba cortada.
   */
  acciones?: React.ReactNode;
  /** Sufijo de los `data-testid` (`enfoque-<testid>-modal`, `-backdrop`, `-cerrar`). */
  testid: string;
  children: React.ReactNode;
}

/**
 * Envoltorio de los controles de la barra. En ESCRITORIO no genera ningun
 * nodo (fragmento): la barra sigue siendo el mismo flex de una sola fila con
 * los mismos hijos directos, pixel por pixel. En telefono si genera una fila
 * propia, alineada a la derecha, bajo el titulo.
 */
const Fila: React.FC<{ esMovil: boolean; children: React.ReactNode }> = ({ esMovil, children }) =>
  esMovil ? (
    <div style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end', gap: 8 }}>
      {children}
    </div>
  ) : (
    <>{children}</>
  );

const ModalEnfoque: React.FC<ModalEnfoqueProps> = ({
  titulo,
  subtitulo,
  onClose,
  testid,
  acciones,
  children,
}) => {
  const esMovil = useEsMovil();

  // Escape cierra. Y mientras el enfoque está abierto se bloquea el scroll del
  // fondo: sin esto, la rueda del ratón mueve la página de atrás en cuanto el
  // puntero sale de la tabla, que es justo la sensación de "perder el hilo".
  useEffect(() => {
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', alPulsar);
    return () => {
      window.removeEventListener('keydown', alPulsar);
      document.body.style.overflow = overflowPrevio;
    };
  }, [onClose]);

  return (
    <div
      data-testid={`enfoque-${testid}-backdrop`}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,23,29,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 110,
        // El relleno del fondo define el hueco y el modal lo llena entero
        // (`height:100%`). Antes era `height:92vh` centrado, y con pantallas
        // bajas el conjunto superaba el alto de la ventana y recortaba la barra
        // de titulo por arriba.
        //
        // En telefono baja a 10: 24 px por lado se comen 48 de los 390 y el
        // modo enfoque existe justamente para darle a la tabla TODO el ancho.
        padding: esMovil ? 10 : 24,
      }}
    >
      <div
        data-testid={`enfoque-${testid}-modal`}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.card,
          borderRadius: esMovil ? 14 : 16,
          border: `1px solid ${colors.border}`,
          boxShadow: '0 20px 48px -14px rgba(20,28,54,.45)',
          width: '100%',
          // Casi toda la pantalla, con aire: es el punto del modo enfoque.
          maxWidth: 1500,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            background: colors.drill.marca,
            color: '#fff',
            padding: esMovil ? '10px 12px' : '12px 18px',
            display: 'flex',
            // Titulo + subtitulo + acciones + "Esc para cerrar" + x en UNA
            // fila de 390 px dejaba el titulo en dos caracteres. En telefono
            // el titulo va arriba y los controles debajo, alineados a la
            // derecha; en escritorio es exactamente la fila de siempre.
            flexDirection: esMovil ? 'column' : 'row',
            alignItems: esMovil ? 'stretch' : 'center',
            gap: esMovil ? 8 : 14,
            flexShrink: 0,
          }}
        >
          <div style={{ flex: esMovil ? 'none' : 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 15.5, letterSpacing: '.01em' }}>{titulo}</div>
            {subtitulo && (
              <div style={{ fontSize: 11.5, opacity: 0.72, fontWeight: 600, marginTop: 2 }}>
                {subtitulo}
              </div>
            )}
          </div>
          {/* En telefono los controles bajan a su propia fila. El envoltorio
              solo existe en movil: en escritorio se usa un fragmento, asi que
              `acciones`, el aviso y la x siguen siendo hijos DIRECTOS del flex
              de la barra y conservan su posicion exacta. */}
          <Fila esMovil={esMovil}>
            {acciones}
            {/* "Esc para cerrar" no aplica sin teclado fisico y solo gasta
                ancho; en telefono se cierra con la x o tocando fuera. */}
            {!esMovil && (
              <span style={{ fontSize: 11, opacity: 0.6, fontWeight: 600, whiteSpace: 'nowrap' }}>
                Esc para cerrar
              </span>
            )}
            <button
              type="button"
              data-testid={`enfoque-${testid}-cerrar`}
              onClick={onClose}
              aria-label="Cerrar"
              style={{
                background: 'rgba(255,255,255,.14)',
                border: 'none',
                borderRadius: 8,
                color: '#fff',
                fontSize: 18,
                lineHeight: 1,
                cursor: 'pointer',
                padding: esMovil ? '0 12px' : '4px 9px',
                // Regla 4: en telefono el area tocable no baja de 44 px.
                ...(esMovil ? { minHeight: 44, minWidth: 44, fontSize: 22 } : null),
              }}
            >
              ×
            </button>
          </Fila>
        </div>

        {/* El hijo se estira: la tabla usa TODO el alto disponible, que es lo
            que no conseguía el "Ver todas" dentro de la página. */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default ModalEnfoque;
